
import { useState, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { getProducts } from '@/lib/supabase/products';
import { linkVideoToProduct, fetchVideoById } from '../services/videoService';
import { ProductData } from '../types/productTypes';
import { getCurrentUserOrganizationId } from '@/lib/supabase/products/organization';
import { Product } from '@/lib/supabase/products/types';

export const useVideoLinking = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);
  const [organizationProducts, setOrganizationProducts] = useState<ProductData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [linkedProduct, setLinkedProduct] = useState<ProductData | null>(null);
  const { toast } = useToast();

  // Load organization products
  useEffect(() => {
    const loadOrganizationProducts = async () => {
      try {
        const orgId = await getCurrentUserOrganizationId();
        if (!orgId) return;

        const products = await getProducts(orgId);
        
        // Convert from Product type to ProductData type
        const convertedProducts: ProductData[] = products.map(product => ({
          id: product.id,
          title: product.name,
          name: product.name,
          image: product.media_urls?.[0] || '',
          price: product.price,
          currency: product.currency,
          type: 'product' as const
        }));
        
        setOrganizationProducts(convertedProducts);
      } catch (error) {
        console.error("Error loading organization products:", error);
      }
    };

    loadOrganizationProducts();
  }, []);

  // Open dialog for linking a product to a video
  const openProductDialog = (videoId: string) => {
    setCurrentVideoId(videoId);
    setIsDialogOpen(true);
  };

  // Link a product to a video
  const linkProductToVideo = async () => {
    if (!currentVideoId || !selectedProductId) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un produit",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await linkVideoToProduct(currentVideoId, selectedProductId);
      
      if (result) {
        toast({
          title: "Succès",
          description: "Le produit a été lié à la vidéo",
          variant: "default"
        });
        
        // Get the linked product details for UI update
        const linkedProductDetails = organizationProducts.find(p => p.id === selectedProductId);
        if (linkedProductDetails) {
          setLinkedProduct(linkedProductDetails);
        }
        
        setIsDialogOpen(false);
        setShouldRefetch(true);
      } else {
        toast({
          title: "Erreur",
          description: "Une erreur est survenue lors de la liaison du produit",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error linking product to video:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la liaison du produit",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Open video in new tab
  const handleOpenVideo = (videoId?: string) => {
    if (!videoId) return;
    window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
  };

  // Reset the refetch flag after data is refreshed
  useEffect(() => {
    if (shouldRefetch) {
      // Wait a bit and then reset the flag
      const timer = setTimeout(() => {
        setShouldRefetch(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [shouldRefetch]);

  return {
    isDialogOpen,
    setIsDialogOpen,
    selectedProductId,
    setSelectedProductId,
    organizationProducts,
    isLoading,
    openProductDialog,
    linkProductToVideo,
    handleOpenVideo,
    shouldRefetch,
    linkedProduct
  };
};
